import { graphql } from 'react-relay';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { expVal } from '@confluence/feature-experiments';

import type { SpaceRestrictionCheckQuery as SpaceRestrictionCheckQueryType } from './__types__/SpaceRestrictionCheckQuery.ts';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

const SpaceRestrictionCheckQuery = graphql`
	query SpaceRestrictionCheckQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			settings {
				routeOverrideEnabled
			}
		}
	}
`;

type SpaceRestrictionCheckProps = {
	asSuperAdmin?: boolean;
	children: (result: {
		error?: ApolloErrorInterop;
		isSpaceNotFound: boolean | undefined;
		isSpaceRestricted: boolean | undefined;
	}) => JSX.Element;
	spaceKey: string;
	contentId?: string;
};

export function SpaceRestrictionCheck({
	asSuperAdmin,
	children,
	spaceKey,
	contentId,
}: SpaceRestrictionCheckProps): JSX.Element | null {
	const [stateSpaceKey] = usePageSpaceKey();

	const { data, error, loading } = useQueryInterop<SpaceRestrictionCheckQueryType>(
		SpaceRestrictionCheckQuery,
		{
			context: asSuperAdmin == undefined ? undefined : { asSuperAdmin },
			errorPolicy: 'all',
			variables: {
				// XXX Though SpaceRestrictionCheckProps defines spaceKey as required,
				// SpaceBaseComponent is not written in TypeScript and it may provide
				// undefined as it is reading spaceKey from redux. While it would result
				// in a better separation of concerns to have a fix in SpaceBaseComponent,
				// such a fix would be riskier.
				spaceKey: spaceKey || stateSpaceKey!,
			},
		},
	);

	let isSpaceNotFound: boolean | undefined;
	let isSpaceRestricted: boolean | undefined;

	if (error) {
		isSpaceNotFound = isSpaceNotFoundError(error);
		isSpaceRestricted = isSpaceRestrictedError(error);
		if (isSpaceNotFound || isSpaceRestricted) {
			markErrorAsHandled(error);
		}
	} else if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		process.env.REACT_SSR &&
		expVal('confluence_frontend_ssr_space_not_found', 'isEnabled', false)
	) {
		isSpaceNotFound = window['__SPACE_NOTFOUND_CHECK_ERROR__'];
		isSpaceRestricted = window['__SPACE_RESTRICTED_CHECK_ERROR__'];
	} else if (data) {
		const errorSwallowed = data.space === null;
		isSpaceNotFound = errorSwallowed && window['__SPACE_NOTFOUND_CHECK_ERROR__'];
		isSpaceRestricted = errorSwallowed && window['__SPACE_RESTRICTED_CHECK_ERROR__'];
	}

	if (loading) {
		return null;
	}

	const contentIsNotViewableBecauseOfSpaceRestrictions =
		Boolean(contentId) && window?.['__SPACE_RESTRICTED_ERROR__'] === contentId;

	return children({
		error,
		isSpaceNotFound,
		isSpaceRestricted: isSpaceRestricted || contentIsNotViewableBecauseOfSpaceRestrictions,
	});
}

export function isSpaceNotFoundError(error: ApolloErrorInterop): boolean {
	return isSpaceError(error, ({ statusCode }) => statusCode === 404 || statusCode === 400);
}

export function isSpaceRestrictedError(error: ApolloErrorInterop): boolean {
	return isSpaceError(error, () => isUnauthorizedError(error));
}

function isSpaceError(
	{ graphQLErrors }: ApolloErrorInterop,
	extensionsPredicate: (extensions: {
		data?: {
			authorized?: boolean;
		};
		statusCode?: number;
	}) => boolean,
) {
	if (graphQLErrors && graphQLErrors.length > 0) {
		for (const { extensions, path } of graphQLErrors) {
			if (
				Array.isArray(path) &&
				path.indexOf('space') !== -1 &&
				extensions &&
				extensionsPredicate(extensions)
			) {
				return true;
			}
		}
	}
	return false;
}
